import dayjs from "dayjs";
import styles from "./event.module.css";
import Link from "next/link";
import AppImage from "../common/AppImage";
import placeholder from "./assests/placeholder.png";
import customParseFormat from "dayjs/plugin/customParseFormat";
import ConvertedTime from "../convertedtime";

import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);
dayjs.extend(customParseFormat);
import { getSEO } from "@/lib/seoHelper";
import { eventUrl, contestUrl } from "@/lib/routes";
import { mapEventStatus } from "@/lib/schemaGenerator";
import { resolveVenueLabel } from "@/components/EventSummary";
import SafeHtml from "@/components/CMS/SafeHtml";

import { trackSelectItem } from "@/lib/analytics";

export default function Event({
  id,
  name,
  slug,
  start_time,
  start_date,
  yet_to_be_announced,
  venue,
  image,
  feature_image,
  feature_images,
  banner,
  price,
  timezone,
  amount,
  currency,
  end_date,
  end_time,
  sold_out,
  max_price,
  organizer,
  status,
  is_contest,
  description,
  is_third_party,
  source_platform_logo,
}) {
  const DEFAULT_TIMEZONE = "Asia/Kolkata";

  const getISODateTime = (date, time, tz) => {
    if (!date) return null;
    const targetTz = tz && typeof tz === "string" ? tz : DEFAULT_TIMEZONE;
    const timeStr = time || "00:00:00";
    try {
      const parsed = dayjs.tz(
        `${date} ${timeStr}`,
        "YYYY-MM-DD HH:mm:ss",
        targetTz,
      );
      if (parsed.isValid()) return parsed.format();
    } catch (e) {
      // fallback
    }
    const fallback = dayjs(`${date} ${timeStr}`);
    return fallback.isValid() ? fallback.format() : null;
  };
  const displayVenueName = yet_to_be_announced
    ? "Venue to be announced"
    : venue?.name || "Venue to be announced";

  const cleanPrice = (val) => {
    if (!val && val !== 0) return "";
    if (typeof val !== "string") return String(val);
    return val.replace(/\s*onwards\s*/gi, "").trim();
  };

  const displayPrice = is_contest
    ? "Enroll"
    : cleanPrice(price) ||
      (amount === "FREE" || amount === 0 || amount === "0"
        ? "FREE"
        : amount
          ? `₹${amount}`
          : "FREE");

  // Resolve cover image — handles both CMS image objects and direct URL strings
  // (third-party events provide a plain string URL in `image`)
  const imageSrc =
    (typeof image === "string" && image) ||
    image?.[0]?.md ||
    image?.[0]?.sd ||
    image?.[0]?.thumbnail ||
    image?.[0]?.url ||
    feature_image?.[0]?.md ||
    feature_image?.[0]?.sd ||
    feature_image?.[0]?.thumbnail ||
    feature_image?.[0]?.url ||
    feature_images?.[0]?.md ||
    feature_images?.[0]?.sd ||
    feature_images?.[0]?.thumbnail ||
    feature_images?.[0]?.url ||
    (typeof feature_image === "string" ? feature_image : null) ||
    (typeof banner === "string" ? banner : null) ||
    image?.md ||
    image?.sd ||
    image?.thumbnail ||
    image?.url ||
    placeholder.src;

  // Resolve platform logo URL — can be a string or a CMS image object
  const platformLogoSrc =
    typeof source_platform_logo === "string"
      ? source_platform_logo
      : source_platform_logo?.[0]?.url ||
        source_platform_logo?.url ||
        null;

  const rawSlug = slug || "";
  const cardHref = is_contest
    ? contestUrl(venue?.city, rawSlug)
    : rawSlug.startsWith("/")
      ? rawSlug
      : `/${rawSlug}`;

  const seo = getSEO("eventCard", {
    eventName: name,
    city: venue?.city,
    venue: displayVenueName,
    date: start_date,
    time: start_time,
    price: price,
    currency: currency,
    max_price: max_price,
    organizer: organizer,
    status: status,
    is_contest: is_contest,
  });
  const cardLinkTitle = yet_to_be_announced
    ? venue?.city
      ? `Book tickets for ${name || "Event"} in ${venue.city}`
      : `Book tickets for ${name || "Event"}`
    : getSEO("common", { eventName: name, venue: venue?.name }).links
        ?.eventTitle;

  const handleCardClick = () => {
    trackSelectItem({
      item: {
        id,
        slug: rawSlug,
        name,
        price: typeof price === "number" ? price : amount ? Number(amount) : 0,
        city: venue?.city,
      },
    });
  };

  return (
    <Link
      href={cardHref}
      className="w-full block h-full min-w-0 max-w-full overflow-hidden"
      id={`event-card-${rawSlug}`}
      title={cardLinkTitle}
      prefetch={false}
      onClick={handleCardClick}
    >
      <div className={styles.card}>
        <div className={styles.cover} style={{ position: "relative" }}>
          <AppImage
            src={imageSrc}
            width={374}
            height={187}
            sizes="(max-width: 576px) 90vw, (max-width: 992px) 45vw, 374px"
            loading="lazy"
            decoding="async"
            style={{ objectFit: "cover" }}
            alt={seo.images.alt || "Event"}
          />
          {is_third_party && platformLogoSrc && (
            <div
              style={{
                position: "absolute",
                bottom: 8,
                right: 8,
                background: "rgba(255,255,255,0.92)",
                borderRadius: 8,
                padding: "3px 6px",
                boxShadow: "0 1px 6px rgba(2,21,38,0.12)",
                display: "flex",
                alignItems: "center",
                backdropFilter: "blur(4px)",
                maxWidth: 80,
              }}
            >
              <img
                src={platformLogoSrc}
                alt="Platform"
                style={{
                  height: 16,
                  width: "auto",
                  maxWidth: 70,
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </div>
          )}
        </div>
        <div className={styles.body}>
          <div className={styles.contentWrap}>
            <span className={styles.venue}>
              {yet_to_be_announced
                ? `Venue to be announced${venue?.city ? `, ${venue.city}` : ""}`
                : resolveVenueLabel(venue) || "Venue to be announced"}
            </span>
            <span className={styles.title}>{name || "N/A"}</span>
            {description ? (
              <span className={styles.description}>
                <SafeHtml html={description} demoteH1 />
              </span>
            ) : null}
          </div>
          <span className={styles.footer}>
            <span className={styles.datetime}>
              <ConvertedTime
                time={start_time}
                timezone={timezone}
                date={start_date}
              />
            </span>
            {sold_out ? (
              <span className={`${styles.price} !bg-red-100 !text-red-500`}>
                Sold Out
              </span>
            ) : status === "completed" ? (
              <span
                className={`${styles.price} !rounded-full !bg-[#E8F8EF] !text-[#1B9E5A]`}
              >
                Completed
              </span>
            ) : (
              <span className={styles.price}>{displayPrice}</span>
            )}
          </span>
        </div>
      </div>
    </Link>
  );
}
